import { useEffect, useRef, useState } from "react";
import { Button, cn } from "@clevis/shared-ui";
import { loadGoogleMaps } from "../lib/googleMaps";

// For lots with no street address yet: tap the map, drop the pin, done.
// The pin feeds routing, navigation and geofenced arrival exactly like a
// geocoded address would.

/* eslint-disable @typescript-eslint/no-explicit-any */
export function PinDrop({
  initial,
  home,
  onSave,
  onCancel,
}: {
  initial: { lat: number; lng: number } | null;
  home: { lat: number; lng: number } | null;
  onSave: (pin: { lat: number; lng: number }) => void | Promise<void>;
  onCancel: () => void;
}) {
  const holder = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const [pin, setPin] = useState(initial);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let dead = false;
    void loadGoogleMaps()
      .then((lib) => {
        if (dead || !holder.current || mapRef.current) return;
        const center = initial ?? home ?? { lat: 36.17, lng: -115.14 };
        const map = new lib.Map(holder.current, {
          center,
          zoom: initial ? 17 : 12,
          mapId: "DEMO_MAP_ID",
          disableDefaultUI: true,
          zoomControl: true,
          mapTypeControl: true,
        });
        mapRef.current = map;
        const place = (pos: { lat: number; lng: number }) => {
          if (!markerRef.current) {
            markerRef.current = new lib.AdvancedMarkerElement({ map, position: pos });
          } else {
            markerRef.current.position = pos;
          }
          setPin(pos);
        };
        if (initial) place(initial);
        map.addListener("click", (e: any) => {
          place({ lat: e.latLng.lat(), lng: e.latLng.lng() });
        });
      })
      .catch((e) => setError((e as Error).message));
    return () => {
      dead = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-2">
      {error ? <p className="text-xs text-danger">{error}</p> : <div ref={holder} className="h-64 w-full rounded-lg" />}
      <div className="flex items-center justify-between">
        <p className={cn("text-xs", pin ? "text-ink" : "text-steel")}>
          {pin ? `Pin: ${pin.lat.toFixed(5)}, ${pin.lng.toFixed(5)}` : "Tap the map where the work is."}
        </p>
        <span className="flex gap-2">
          <Button variant="ghost" className="px-3 py-1 text-xs" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            className="px-3 py-1 text-xs"
            disabled={!pin}
            busy={busy}
            onClick={async () => {
              if (!pin) return;
              setBusy(true);
              try {
                await onSave(pin);
              } finally {
                setBusy(false);
              }
            }}
          >
            Save pin
          </Button>
        </span>
      </div>
    </div>
  );
}
